import { useTranslation } from "react-i18next";
import { useSettingsStore } from "../store/SettingsStore";
import { useUserMenuUi } from "./userMenuUiStore";

/**
 * Always-visible marker that the app is showing the shared demo dataset.
 *
 * Why viewport-fixed rather than a badge in the header: the header is a normal
 * flow element that crops itself with a clip-path on scroll, so anything inside
 * it scrolls out of view. The whole point of this hint is that demo figures are
 * never mistaken for the user's real production data, which means it has to
 * survive scrolling.
 *
 * Why the bottom-left corner: every other corner is taken. Bottom-right holds
 * the chat bubble and the Service Portal link, `bottom-0` full-width holds the
 * mobile tab bar, and a fixed strip along the top would have to be folded into
 * `--app-header-height` and every sticky offset derived from it. The corner
 * keeps the hint permanent without touching that layout contract.
 *
 * `z-40` puts it above page content but below the z-50 overlays (chat panel,
 * modals), so it never covers a dialog.
 */
const DemoModeBadge = () => {
  const { t } = useTranslation();
  const demoMode = useSettingsStore((s) => s.demoMode);
  const autoDemoMode = useSettingsStore((s) => s.autoDemoMode);
  const autoDemoReason = useSettingsStore((s) => s.autoDemoReason);

  if (!demoMode && !autoDemoMode) return null;

  // Automatic demo (no org/workspace/machines) reads differently from a
  // deliberate manual switch: the user did not choose this, so the hint
  // explains *why* they are seeing demo data. The manual case keeps the
  // original wording.
  const isAutomatic = autoDemoMode && !demoMode;
  const label = isAutomatic
    ? t("demoBadge.autoLabel", { defaultValue: "Demo data (automatic)" })
    : t("demoBadge.label", { defaultValue: "Demo data" });
  // Each automatic reason gets its own wording — telling a user with no
  // organisation that they "have no workspace" is confusing. `no-machines` and
  // `no-workspace` keep their strings; `no-org` falls back to the no-workspace
  // text only if its own key is somehow missing.
  const autoTooltip = () => {
    switch (autoDemoReason) {
      case "no-machines":
        return t("demoBadge.autoTooltipNoMachines", {
          defaultValue:
            "Your account has no machines yet, so demo data is shown. Click to open the user menu.",
        });
      case "no-org":
        return t("demoBadge.autoTooltipNoOrg", {
          defaultValue:
            "Your account is not linked to an organisation yet, so demo data is shown. Click to open the user menu.",
        });
      case "no-workspace":
      default:
        return t("demoBadge.autoTooltipNoWorkspace", {
          defaultValue:
            "Your account has no workspace yet, so demo data is shown. Click to open the user menu.",
        });
    }
  };
  const tooltip = isAutomatic
    ? autoTooltip()
    : t("demoBadge.tooltip", {
        defaultValue:
          "Showing the shared demo dataset, not your organisation's data. Click to open the user menu and switch demo mode off.",
      });

  // the demo switch is in the avatar menu of the header: open it
  return (
    <button
      type="button"
      onClick={() => useUserMenuUi.getState().setOpen(true)}
      title={tooltip}
      // `bottom-20` clears the mobile tab bar on machine and line views; the
      // bar is `sm:hidden`, so the desktop offset drops back down.
      className="fixed bottom-20 left-4 sm:bottom-4 sm:left-4 lg:left-22 z-40 flex items-center gap-2 rounded-full border border-violet-300/60 bg-violet-600 px-3 py-1.5 text-white shadow-lg transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2"
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full bg-white/90"
        aria-hidden="true"
      />
      <span className="text-xs font-semibold uppercase tracking-wide">
        {label}
      </span>
    </button>
  );
};

export default DemoModeBadge;
